import { Button, Col, Row } from "antd";
import { useEffect, useMemo, useState } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { Link, useParams } from "react-router-dom";
import styled from "styled-components";
import Page from "../../Common/Page";
import LoaderContainer from "../../Common/Page/LoaderContainer";
import PermissionCheck from "../../Common/PermissionCheck";
import FormControl from "../../components/Form/FormControl";
import { StyledFormInputs } from "../../components/Form/StyledFormInputs";
import { useFetchDivision } from "../../entities/api";
import { useDivisionService } from "~pages/Divisions/hooks/useDivisionService.ts";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import { useGetOrgData } from "../../hooks/useGetOrgData";
import type { IDivision } from "./types";

const PermissionName = PermissionNames.DIVISION;

interface IFormInput {
  nameLocal: string;
  nameLatin: string;
}

const defaultDivision: IDivision = {
  abbreviationLatin: "",
  abbreviationLocal: "",
  added: "",
  conference: { id: "", resourceType: "" },
  conferenceId: "",
  divisionId: "",
  images: [],
  nameLatin: "",
  nameLocal: "",
  organization: { id: "", resourceType: "" },
  organizationId: "",
  updated: "",
};
const ManageDivision = () => {
  const { sport, organizationId } = useGetOrgData();
  const { divisionId, conferenceId, customerId } = useParams();
  const { formatMessage } = useIntl();
  const [data, setData] = useState<IDivision>(defaultDivision);
  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<IFormInput>();
  const { data: divisionRes, isLoading: isDivisionLoading } = useFetchDivision({ divisionId });
  const { createDivision, isCreatePending, updateDivision, isUpdatePending } = useDivisionService();

  useEffect(() => {
    reset({
      nameLocal: data.nameLocal,
      nameLatin: data.nameLatin,
    });
  }, [data, reset]);

  useEffect(() => {
    if (divisionId && divisionRes) {
      setData(divisionRes?.data?.[0]);
    }
  }, [divisionId, divisionRes]);

  const title = useMemo(() => {
    return divisionId
      ? `${formatMessage({
          id: "manage",
          defaultMessage: "Manage",
        })} ${data.nameLocal ? `- ${data.nameLocal}` : ""}`
      : `${formatMessage({
          id: "division.add",
          defaultMessage: "Create Division",
        })}`;
  }, [divisionId, formatMessage, data.nameLocal]);

  const onSubmit: SubmitHandler<IFormInput> = async (data) => {
    if (divisionId) {
      await updateDivision(divisionId, data);
      return;
    }

    await createDivision(data);
  };

  return (
    <Page title={title} tabTitle={title}>
      <LoaderContainer loading={isDivisionLoading || isCreatePending || isUpdatePending}>
        <Content>
          <form style={{ marginLeft: 20 }} onSubmit={handleSubmit(onSubmit)}>
            <Row style={{ maxWidth: 960 }} gutter={16}>
              <Col span={12}>
                <Row gutter={16}>
                  <Col span={16}>
                    <FormControl
                      labelText="name.local"
                      name="nameLocal"
                      labelTooltip={formatMessage(
                        {
                          id: "form.help.local.name.item",
                        },
                        {
                          item: formatMessage({
                            id: "division",
                          }),
                        },
                      )}
                      rules={{
                        required: formatMessage(
                          {
                            id: "form.help.required.field",
                          },
                          {
                            field: formatMessage({
                              id: "name.local",
                            }),
                          },
                        ),
                      }}
                      control={control}
                      render={({ field }) => <StyledFormInputs {...field} />}
                      errors={errors}
                    />
                  </Col>
                </Row>
              </Col>
              <Col span={12}>
                <Row gutter={16}>
                  <Col span={16}>
                    <FormControl
                      labelText="name.latin"
                      name="nameLatin"
                      labelTooltip={formatMessage(
                        {
                          id: "form.help.latin.name.item",
                        },
                        {
                          item: formatMessage({
                            id: "division",
                          }),
                        },
                      )}
                      control={control}
                      render={({ field }) => <StyledFormInputs {...field} />}
                    />
                  </Col>
                </Row>
              </Col>
            </Row>
            <PermissionCheck
              permissionKeys={[PermissionName]}
              permissionOptions={divisionId ? [PermissionOperations.UPDATE] : [PermissionOperations.CREATE]}
            >
              <Button htmlType="submit" type="primary">
                {divisionId ? formatMessage({ id: "division.update" }) : formatMessage({ id: "division.add" })}
              </Button>
            </PermissionCheck>
            <Link to={`/${customerId}/admin/${sport}/${organizationId}/conferences/${conferenceId}/divisions`}>
              <Button style={{ marginLeft: 30, marginTop: 10 }} type="primary">
                {formatMessage({ id: "close" })}
              </Button>
            </Link>
          </form>
        </Content>
      </LoaderContainer>
    </Page>
  );
};
const Content = styled.div`
  display: flex;

  .container {
    margin: 0;
  }
`;
export default ManageDivision;
